import type { AnchorSpec, RecurrenceRule } from '@dl/shared';

import { calendarBag, cycleLabel, cyclePosition } from './cycles.js';
import { mod } from './leap.js';
import {
  type Calendar,
  type CycleBag,
  type CycleDefinition,
  type CycleInstance,
  type OpenCycleInstance,
} from './types.js';

/**
 * `recurrence.ts` — virtual-instance synthesis (B3, #236; `06-timeline.md`
 * §10.3).
 *
 * A recurring event (festival, anniversary, eclipse) lives as a
 * `RecurrenceRule` on a parent Lore (the `recurs_as` fact, B1/B2). Rather
 * than materialize every occurrence as an entity (instance explosion over a
 * millennium of Yules), the rule synthesizes **virtual instances** on demand:
 * Event-shaped records with a computed `occurred_at` and the rule's inherited
 * defaults (`at_location`, `participants`). Virtual instances have **no
 * entity id, no fact stream, no reveal log** — they are pure computed records
 * (§10.3). Materialization into a real Event is a separate slice (B4, #237).
 *
 * The single public entry is {@link virtualInstances}: given a calendar, a
 * rule, and a canonical-day `[fromDay, toDay]` range (inclusive both ends),
 * it returns the firing occurrences within the range.
 *
 * **Bounded by the range — O(occurrences), not O(days).** The engine never
 * scans the day axis. It resolves the anchored cycle's *constant structural
 * period* (one pass through the cycle's instance sequence — a year is always
 * 365 structural days, a week always 7) and the firing day of occurrence 0,
 * then steps occurrence-by-occurrence with a closed-form `phase + k*stride`.
 * A 10000-year query touches ~10000 occurrences, not 3.65M days, so a huge
 * range cannot explode.
 *
 * **Anchor kinds (§10.1).**
 *   - `day_of_cycle` — a fixed `offset` into each cycle instance (day 184 of
 *     the year). Stride = the cycle's period; firing = instance-start + offset.
 *   - `nested_position` — a `path` of `(cycle, instance)` steps drilling into
 *     nested cycles ("25 Reaping" → month="Reaping", day-of-month=25). The
 *     path resolves to a fixed within-period offset (the same offset recurs
 *     every period); stride = the rule's cycle period.
 *   - `labeled` — fires whenever a named cycle's labeled position recurs
 *     ("Wood Rat" stem-branch year). The labeled position has a fixed offset
 *     within the cycle's period; stride = that cycle's period.
 *
 * **`every_n` (§10.1).** Applies to the *occurrence* sequence, anchored to
 * occurrence index 0 (not the query range): `every_n: 2` keeps occurrences
 * 0, 2, 4, …, so the parity is stable regardless of where the query window
 * starts.
 *
 * **Leap interaction.** The bag the engine reads is leap-unaware (the same
 * structural layer `cyclePosition` works over — see `cycles.ts`). A nested
 * year/month cycle resolves to its *structural* period (every year the same
 * declared length); the leap-aware year/month walk stays in `convert.ts`.
 * Recurrence anchors therefore fire on the structural cycle position, which
 * is the §4.2 contract ("a rule anchors against a cycle and queries that
 * cycle's day-count, not the global cursor").
 *
 * **Per-cycle intercalary policy (§4.2).** `cyclePosition` already routes the
 * anchored cycle's structural position; the per-cycle non-advancing
 * intercalary phase-advance is the A2/A3 engine's concern (#231/#232). The
 * same `cyclePosition`/`cycleLabel` seam this module reads honors it
 * transparently — a non-advancing intercalary day does not shift the
 * *structural period* this module reads from the bag (the period is the sum
 * of one pass of instance lengths, intercalary policy only re-phases a
 * parallel atomic cycle), so a `day_of_cycle`/`nested_position` firing day
 * stays put. (The `open-cycles.test.ts` §4.2 Shire-week-vs-Harptos-tenday
 * suite pins that interaction at the engine layer.)
 *
 * **Open cycles + suspense (§10.4, B6 #239).** Recurrence anchors against
 * *closed* cycle instances only. An `open` cycle has no fixed structural
 * period — its instances' lengths are determined retrospectively (§4.1,
 * ASOIAF seasons / WoT Ages) — so `cyclePeriod` returns `null` for it and the
 * closed-form `phase + k*stride` walk does not apply. Instead, a rule
 * anchored against an open cycle fires **once per CLOSED instance** of that
 * cycle (see {@link resolveOpenCycleFirings}):
 *   - `day_of_cycle { offset }` — fires at `start_day + offset` of each closed
 *     instance, but only when that day is inside the now-known span
 *     (`< end_day`). A still-open instance (`end_day` unset) fires nothing —
 *     it is held in **suspense** until `close_at(day)` fills its `end_day`;
 *     thereafter it fires in earnest within `[start_day, end_day)`.
 *   - `labeled { cycle, label }` (cycle open) — fires at the `start_day` of
 *     each closed instance whose `name === label`.
 *   - `nested_position` — undefined against an open cycle (no nested
 *     structure); synthesizes nothing.
 * `every_n` applies over the closed-instance firing sequence in start-day
 * order. The auditor surfaces a notice when a rule references an open cycle
 * (`recurrence.open_cycle`, B6) — see {@link openCyclesReferencedBy}.
 *
 * Pure and deterministic: no `Date`, no `Math.random`, no functions in data.
 */

// ---------- public records ----------

/** An intra-day position degraded to fixed `seconds_into_day` (D15/D18). */
export interface VirtualSubDay {
  readonly seconds_into_day: number;
}

/** One endpoint of a virtual occurrence: a canonical day + optional sub-day. */
export interface VirtualPoint {
  readonly day: number;
  readonly seconds_into_day?: number;
}

/**
 * The computed `occurred_at` of a virtual instance — a point (no/1-day
 * duration) or a range (multi-day duration). Mirrors the in-world
 * `game_date` / `game_date_range` shapes the materialized Event would carry,
 * but is a plain computed value (not a `VisibleFact` — the projection
 * chokepoint is untouched).
 */
export type VirtualOccurredAt =
  | { readonly kind: 'point'; readonly day: number; readonly seconds_into_day?: number }
  | { readonly kind: 'range'; readonly from: VirtualPoint; readonly to: VirtualPoint };

/**
 * A synthesized virtual instance of a recurring event (§10.3). Event-shaped
 * but *not* an entity: no `id`, no `facts`, no `reveals`. The only handle is
 * the structural `occurrence_index` — a deterministic ordinal (which firing
 * in the sequence), NOT an entity id (synthesized ids are §14 deferred).
 */
export interface VirtualInstance {
  /** Computed firing date — point or range (from the rule's `duration`). */
  readonly occurred_at: VirtualOccurredAt;
  /** Inherited from the rule (`at_location`) — defaults onto each instance. */
  readonly at_location?: string;
  /** Inherited from the rule (`participants`). */
  readonly participants?: readonly string[];
  /**
   * The 0-based structural occurrence index (which firing in the sequence,
   * anchored to occurrence 0 at/after the cycle origin). A deterministic
   * ordinal handle — not an entity id.
   */
  readonly occurrence_index: number;
}

// ---------- bag helpers (read-only; period/offset resolution) ----------

function findCycle(bag: CycleBag, name: string): CycleDefinition | undefined {
  return bag.find((c) => c.name === name);
}

/** The instances of a `NestedPattern` in one pass, as `{ length, name? }`. */
function patternEntries(
  pattern: Extract<CycleDefinition['structure'], { kind: 'nested' }>['pattern'],
): { length: number; name?: string }[] {
  if (pattern.kind === 'regular') {
    return [{ length: pattern.children_per_instance }];
  }
  return pattern.per_instance.map((inst: CycleInstance) => {
    const entry: { length: number; name?: string } = { length: inst.length };
    if (inst.name !== undefined) entry.name = inst.name;
    return entry;
  });
}

/**
 * Per-instance day lengths of a nested cycle in one pass, resolving the
 * `based_on` chain down to the implicit `day` cycle. Returns `null` for a
 * malformed chain. (A read-only mirror of the resolution `cycles.ts` does
 * internally — needed here to compute a cycle's *period* and to resolve a
 * `nested_position` within-period offset, neither of which `cycles.ts`
 * exposes. Kept in this module's lane; `cycles.ts` is not modified.)
 */
function instanceDayLengths(
  bag: CycleBag,
  cycle: CycleDefinition,
  seen: ReadonlySet<string> = new Set(),
): { length: number; name?: string }[] | null {
  if (cycle.structure.kind !== 'nested') return null;
  if (seen.has(cycle.name)) return null;
  const nextSeen = new Set(seen).add(cycle.name);

  const { based_on, pattern } = cycle.structure;
  if (based_on === 'day') {
    return patternEntries(pattern);
  }

  const child = findCycle(bag, based_on);
  if (child === undefined) return null;
  const childLengths = instanceDayLengths(bag, child, nextSeen);
  if (childLengths === null || childLengths.length === 0) return null;

  const childCount = childLengths.length;
  const childDayAt = (childIndex: number): number => childLengths[mod(childIndex, childCount)]!.length;

  const entries: { length: number; name?: string }[] = [];
  let childCursor = 0;
  for (const inst of patternEntries(pattern)) {
    let days = 0;
    let consumed = 0;
    while (consumed < inst.length) {
      days += childDayAt(childCursor);
      childCursor++;
      consumed++;
    }
    const entry: { length: number; name?: string } = { length: days };
    if (inst.name !== undefined) entry.name = inst.name;
    entries.push(entry);
  }
  return entries;
}

/**
 * The structural period of a named cycle in canonical days — the day count
 * of one full pass through the cycle's instance sequence. Constant by
 * construction (the bag is leap-unaware), which is exactly what lets the
 * occurrence walk be closed-form `phase + k*period`.
 *
 * Returns `null` for an unknown cycle, an `open` cycle (A3 #232 seam — no
 * known period yet), or a malformed nested chain.
 */
function cyclePeriod(bag: CycleBag, cycleName: string): number | null {
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined) return null;
  switch (cycle.structure.kind) {
    case 'atomic':
      return cycle.structure.period_days;
    case 'open':
      // Open cycles host no recurrence anchors until closed (§10.4).
      return null;
    case 'nested': {
      const lengths = instanceDayLengths(bag, cycle);
      if (lengths === null || lengths.length === 0) return null;
      const period = lengths.reduce((sum, e) => sum + e.length, 0);
      return period > 0 ? period : null;
    }
  }
}

/**
 * The cumulative day offset, within one pass of a *nested* cycle, at the
 * start of the instance addressed by `instance` (a name or an ordinal). For
 * an ordinal, the index is taken modulo the pass length (a single pass
 * repeats). Returns `null` if the named instance is not found.
 */
function nestedInstanceStartOffset(
  lengths: readonly { length: number; name?: string }[],
  instance: string | number,
): number | null {
  let target: number;
  if (typeof instance === 'number') {
    target = mod(instance, lengths.length);
  } else {
    const found = lengths.findIndex((e) => e.name === instance);
    if (found < 0) return null;
    target = found;
  }
  let offset = 0;
  for (let i = 0; i < target; i++) offset += lengths[i]!.length;
  return offset;
}

/**
 * Resolve a `nested_position` `path` to its fixed within-period offset of
 * the rule's anchored cycle. The path drills successively: each step names a
 * (sub)cycle and the instance within it; a trailing `{ cycle: 'day',
 * instance: N }` step addresses the N-th day (1-indexed) within the deepest
 * named instance. Returns `null` for any unresolvable step.
 *
 * Example (`06-timeline.md` §10.2): `(month="Reaping"), (day-of-month=25)`
 * on a year cycle → offset of "Reaping" within the year + (25 - 1) days.
 */
function nestedPositionOffset(bag: CycleBag, path: AnchorSpecPath): number | null {
  let offset = 0;
  for (const step of path) {
    if (step.cycle === 'day') {
      // A day step addresses the 1-indexed day within the current instance.
      if (typeof step.instance !== 'number') return null;
      offset += step.instance - 1;
      continue;
    }
    const cycle = findCycle(bag, step.cycle);
    if (cycle === undefined || cycle.structure.kind !== 'nested') return null;
    const lengths = instanceDayLengths(bag, cycle);
    if (lengths === null) return null;
    const stepOffset = nestedInstanceStartOffset(lengths, step.instance);
    if (stepOffset === null) return null;
    offset += stepOffset;
  }
  return offset;
}

type AnchorSpecPath = Extract<AnchorSpec, { kind: 'nested_position' }>['path'];

// ---------- firing-plan resolution ----------

/**
 * A firing plan: the closed-form occurrence stream. Occurrence `k` fires on
 * canonical day `originDay + k * stride`; only every `every_n`-th occurrence
 * (anchored to k=0) actually fires. `null` means the rule synthesizes no
 * instances (unknown/open cycle, unresolvable anchor).
 */
interface FiringPlan {
  /** Firing day of occurrence index 0. */
  readonly originDay: number;
  /** Canonical-day distance between consecutive occurrences (> 0). */
  readonly stride: number;
}

function resolveFiringPlan(calendar: Calendar, rule: RecurrenceRule): FiringPlan | null {
  const bag = calendarBag(calendar);
  const anchor = rule.anchor;

  if (anchor.kind === 'labeled') {
    // Labeled anchors recur each time the named cycle's labeled position
    // repeats. Find the first day (from cycle origin) carrying the label
    // within one period; stride = that cycle's period.
    const period = cyclePeriod(bag, anchor.cycle);
    if (period === null) return null;
    for (let d = 0; d < period; d++) {
      if (cycleLabel(calendar, anchor.cycle, d) === anchor.label) {
        return { originDay: d, stride: period };
      }
    }
    return null; // label never occurs in this cycle
  }

  // `day_of_cycle` and `nested_position` both fire once per instance of the
  // rule's `cycle`, at a fixed within-period offset. The cycle origin
  // (instance 0 start) is the day whose position resets — for the bag the
  // engine reads, instance 0 begins at canonical day 0 (the §4.1 "runs from
  // day 0" default), so the within-period offset IS the origin day.
  const period = cyclePeriod(bag, rule.cycle);
  if (period === null) return null;

  let offset: number | null;
  if (anchor.kind === 'day_of_cycle') {
    offset = anchor.offset;
  } else {
    offset = nestedPositionOffset(bag, anchor.path);
  }
  if (offset === null) return null;

  // Defensive: confirm the offset day actually resolves a position in the
  // anchored cycle (guards a malformed bag / out-of-range offset).
  const pos = cyclePosition(calendar, rule.cycle, mod(offset, period));
  if (pos === null) return null;

  return { originDay: offset, stride: period };
}

// ---------- open cycles (§10.4 suspense) ----------

/** The open-cycle structure of a named cycle, or `null` when not open. */
function openStructure(
  bag: CycleBag,
  cycleName: string,
): Extract<CycleDefinition['structure'], { kind: 'open' }> | null {
  const cycle = findCycle(bag, cycleName);
  if (cycle === undefined || cycle.structure.kind !== 'open') return null;
  return cycle.structure;
}

/**
 * The cycle names a `RecurrenceRule` references that resolve to an `open`
 * cycle on `calendar` (§10.4). Both `rule.cycle` (the anchored cycle) and a
 * `labeled` anchor's `anchor.cycle` are inspected. Sorted + de-duplicated for
 * a stable auditor advisory. An unknown cycle is NOT an open cycle (a distinct
 * `ref`-shaped concern), so it is excluded.
 *
 * This is the shared seam the auditor's `recurrence.open_cycle` rule (B6 #239)
 * and the suspense path read — one definition of "references an open cycle".
 */
export function openCyclesReferencedBy(calendar: Calendar, rule: RecurrenceRule): string[] {
  const bag = calendarBag(calendar);
  const names = new Set<string>();
  if (openStructure(bag, rule.cycle) !== null) names.add(rule.cycle);
  if (rule.anchor.kind === 'labeled' && openStructure(bag, rule.anchor.cycle) !== null) {
    names.add(rule.anchor.cycle);
  }
  return [...names].sort();
}

/** Whether the rule references any open cycle on this calendar (§10.4). */
export function referencesOpenCycle(calendar: Calendar, rule: RecurrenceRule): boolean {
  return openCyclesReferencedBy(calendar, rule).length > 0;
}

/**
 * The firing days of a rule anchored against an OPEN cycle, within
 * `[fromDay, toDay]` (§10.4). Fires once per CLOSED instance (suspense for
 * open ones); `every_n` keeps every n-th firing in start-day order, anchored
 * to occurrence 0. Returns the firing days paired with their occurrence index
 * (the start-day-ordered ordinal across all candidate instances, BEFORE the
 * `every_n` filter — so the parity is stable as instances close).
 *
 * Returns `null` when the rule's anchor is not addressable against an open
 * cycle (`nested_position` — no nested structure), so the caller can fall back
 * to "no firings".
 */
function resolveOpenCycleFirings(
  calendar: Calendar,
  rule: RecurrenceRule,
  fromDay: number,
  toDay: number,
): { day: number; occurrenceIndex: number }[] | null {
  const bag = calendarBag(calendar);
  const anchor = rule.anchor;

  // The open cycle the firings step through: a `labeled` anchor names its own
  // cycle; `day_of_cycle` uses the rule's cycle.
  const openCycleName = anchor.kind === 'labeled' ? anchor.cycle : rule.cycle;
  const open = openStructure(bag, openCycleName);
  if (open === null) return null;

  // `nested_position` has no meaning against an open cycle (no nested
  // sub-structure to drill) — synthesize nothing.
  if (anchor.kind === 'nested_position') return [];

  // Candidate instances in start-day order (the declared array is NOT assumed
  // sorted; the occurrence index keys off the canonical start-day order so it
  // is stable regardless of declaration order). Index ties (duplicate
  // start_day) break by declared order for determinism.
  const ordered: OpenCycleInstance[] = [...open.instances]
    .map((inst, declaredIndex) => ({ inst, declaredIndex }))
    .sort((a, b) =>
      a.inst.start_day !== b.inst.start_day
        ? a.inst.start_day - b.inst.start_day
        : a.declaredIndex - b.declaredIndex,
    )
    .map((e) => e.inst);

  const everyN = rule.every_n; // schema-guaranteed positive int
  const out: { day: number; occurrenceIndex: number }[] = [];

  ordered.forEach((inst, occurrenceIndex) => {
    // SUSPENSE: a still-open instance (no `end_day`) hosts no firings — its
    // length is unknown (§10.4). Skip it; it begins firing once closed.
    if (inst.end_day === undefined) return;

    // `every_n` keeps occurrences 0, n, 2n, … in start-day order.
    if (mod(occurrenceIndex, everyN) !== 0) return;

    const firingDay = openCycleFiringDay(calendar, anchor, inst);
    if (firingDay === null) return;
    // The firing must fall inside the instance's now-known span [start, end).
    if (firingDay < inst.start_day || firingDay >= inst.end_day) return;
    if (firingDay < fromDay || firingDay > toDay) return;

    out.push({ day: firingDay, occurrenceIndex });
  });

  return out;
}

/**
 * The firing day of an `AnchorSpec` within a single (closed) open-cycle
 * instance:
 *   - `day_of_cycle { offset }` → `start_day + offset`.
 *   - `labeled { label }` → the instance's `start_day` when its name matches
 *     the label, else `null` (this instance does not host the labeled firing).
 * `nested_position` is filtered upstream.
 */
function openCycleFiringDay(
  _calendar: Calendar,
  anchor: AnchorSpec,
  inst: OpenCycleInstance,
): number | null {
  switch (anchor.kind) {
    case 'day_of_cycle':
      return inst.start_day + anchor.offset;
    case 'labeled':
      return inst.name === anchor.label ? inst.start_day : null;
    case 'nested_position':
      return null;
  }
}

// ---------- occurred_at construction ----------

function pointOf(day: number, subDay: VirtualSubDay | undefined): VirtualOccurredAt {
  if (subDay !== undefined) {
    return { kind: 'point', day, seconds_into_day: subDay.seconds_into_day };
  }
  return { kind: 'point', day };
}

function occurredAtFor(startDay: number, rule: RecurrenceRule): VirtualOccurredAt {
  const duration = rule.duration;
  const subDay = duration?.sub_day;
  const days = duration?.days;

  // A multi-day duration produces an inclusive range [start, start+days-1].
  // A 1-day (or absent) duration is a single-day point — a degenerate range
  // collapses to a point to mirror the `game_date` (point) shape the
  // materialized Event would carry.
  if (days !== undefined && days > 1) {
    const from: VirtualPoint =
      subDay !== undefined ? { day: startDay, seconds_into_day: subDay.seconds_into_day } : { day: startDay };
    const to: VirtualPoint = { day: startDay + days - 1 };
    return { kind: 'range', from, to };
  }
  return pointOf(startDay, subDay);
}

// ---------- public entry ----------

/**
 * Synthesize the virtual instances of a `RecurrenceRule` whose firing date
 * falls within the inclusive canonical-day range `[fromDay, toDay]`
 * (`06-timeline.md` §10.3).
 *
 * Pure and deterministic. Bounded by the range: the occurrence stream is
 * walked in closed form (`originDay + k*stride`), so the work is
 * O(occurrences-in-range), never O(days-in-range). Returns `[]` (no throw)
 * for an unknown/open cycle, an unresolvable anchor, or an inverted range —
 * the graceful-degrade contract.
 */
export function virtualInstances(
  calendar: Calendar,
  rule: RecurrenceRule,
  fromDay: number,
  toDay: number,
): VirtualInstance[] {
  if (toDay < fromDay) return [];

  // §10.4 suspense — a rule anchored against an OPEN cycle does not have a
  // fixed period (its instances' lengths are determined retrospectively), so
  // the closed-form stride walk does not apply. Fire once per CLOSED instance;
  // still-open instances are held in suspense (no firings) until `close_at`.
  if (referencesOpenCycle(calendar, rule)) {
    const firings = resolveOpenCycleFirings(calendar, rule, fromDay, toDay);
    if (firings === null) return [];
    return firings.map(({ day, occurrenceIndex }) =>
      applyDefaults(
        { occurred_at: occurredAtFor(day, rule), occurrence_index: occurrenceIndex },
        rule,
      ),
    );
  }

  const plan = resolveFiringPlan(calendar, rule);
  if (plan === null) return [];

  const { originDay, stride } = plan;
  const everyN = rule.every_n; // schema-guaranteed positive int
  // The step between *firing* occurrences in canonical days.
  const step = stride * everyN;

  // First firing-step index `s` whose day >= fromDay. The occurrence stream
  // is bi-infinite (occurrence 0 sits at `originDay`; negative `s` reaches
  // deep history), bounded on both sides by the query range — so `s` is NOT
  // clamped to 0. Solve `originDay + s*step >= fromDay`, i.e.
  // `s = ceil((fromDay - originDay) / step)`. Closed-form — no day scan.
  const firstStepIndex = Math.ceil((fromDay - originDay) / step);

  const out: VirtualInstance[] = [];
  for (let s = firstStepIndex; ; s++) {
    const day = originDay + s * step;
    if (day > toDay) break;
    const occurrenceIndex = s * everyN;
    const instance: VirtualInstance = {
      occurred_at: occurredAtFor(day, rule),
      occurrence_index: occurrenceIndex,
    };
    const withDefaults = applyDefaults(instance, rule);
    out.push(withDefaults);
  }
  return out;
}

function applyDefaults(instance: VirtualInstance, rule: RecurrenceRule): VirtualInstance {
  const result: { -readonly [K in keyof VirtualInstance]: VirtualInstance[K] } = {
    occurred_at: instance.occurred_at,
    occurrence_index: instance.occurrence_index,
  };
  if (rule.at_location !== undefined) result.at_location = rule.at_location;
  if (rule.participants !== undefined) result.participants = [...rule.participants];
  return result;
}
